'use client';

import { useState } from 'react';
import AnimateOnScroll from '@/components/AnimateOnScroll';
import GridSection from '@/components/site/GridSection';
import {
  CONTACT_SALES_URL,
  CURRENCY,
  EST_LINK_STEPS,
  LIMITS,
  PREMIUM_REQUEST_URL,
  REGISTER_URL,
  TIERS,
  USAGE_DIMENSIONS,
  type UsageDimension,
} from './data';

/** Muted grays of the ink band (match the dark-band sections elsewhere). */
const SOFT = 'text-[#a6a6b8]';
const DIM = 'text-[#73738a]';

const fmt = (n: number) => n.toLocaleString('en-US');

/** The ∞ glyph is tiny at the readout's 11px mono size — render it a few
 *  points larger (baseline-nudged) so it carries the same optical weight as
 *  the digits beside it. */
function StepValue({ step }: { step: string }) {
  if (step !== '∞') return <>{step}</>;
  return <span className="align-[-0.08em] text-[15px] leading-none">&infin;</span>;
}

/** Free → Premium → Enterprise readout with a trailing owner-page link. */
function TierReadout({ dim }: { dim: UsageDimension }) {
  return (
    <span className="font-jetbrains text-[11px] tracking-[0.08em] text-brand-400">
      {dim.steps.map((step, i) => (
        <span key={i}>
          {i > 0 && <span className={DIM}> &rarr; </span>}
          <StepValue step={step} />
        </span>
      ))}
      {dim.href && (
        <a href={dim.href} className={`ml-2.5 ${DIM} hover:text-white`}>
          {dim.href}
        </a>
      )}
    </span>
  );
}

function InstrumentCell({ dim }: { dim: UsageDimension }) {
  return (
    <div className="flex min-h-[168px] flex-col gap-2.5 bg-ink px-[26px] py-6">
      <span className="flex flex-wrap items-center gap-2.5 font-jetbrains text-[11px] font-semibold tracking-[0.14em] text-white">
        {dim.term.toUpperCase()}
        {dim.nonLimit && (
          <span className="pill-shimmer relative overflow-hidden rounded bg-data-green/15 px-2 py-[3px] text-[9.5px] tracking-[0.09em] text-data-green">
            NEVER METERED
          </span>
        )}
      </span>
      <p className={`text-sm leading-[1.6] text-pretty ${SOFT}`}>{dim.copy}</p>
      <span className="mt-auto">
        <TierReadout dim={dim} />
      </span>
    </div>
  );
}

/** Active-links slider + domain stepper → matched tier, reading the same
 *  LIMITS const as the table — no second set of numbers. */
function Estimator() {
  const [linksIdx, setLinksIdx] = useState(3); // 100 — the Free cap
  const [domains, setDomains] = useState(1);

  const links = EST_LINK_STEPS[linksIdx];
  const premium = TIERS.find((t) => t.id === 'premium')?.price;
  const domainsLabel = `${domains} ${domains === 1 ? 'domain' : 'domains'}`;

  let tier: string;
  let tierColor = 'text-white';
  let why: string;
  let cta: { label: string; href: string };
  if (links <= LIMITS.links.free && domains <= LIMITS.domains.free) {
    tier = 'Free';
    why = `${fmt(links)} active links and ${domainsLabel} fit inside Free — ${CURRENCY}0, unmetered traffic, no credit card, no time limit.`;
    cta = { label: 'Start for free', href: REGISTER_URL };
  } else if (domains <= LIMITS.domains.premium) {
    tier = 'Premium';
    tierColor = 'text-brand-400';
    why = `${fmt(links)} active links and ${domainsLabel} fit Premium — links are unmetered from here, ${CURRENCY}${premium?.monthly}/mo or ${CURRENCY}${premium?.annual}/mo billed annually.`;
    cta = { label: 'Request Premium', href: PREMIUM_REQUEST_URL };
  } else {
    tier = 'Enterprise';
    why = `${fmt(links)} active links and ${domainsLabel} outgrow Premium — custom domains, custom terms, a human on the other end.`;
    cta = { label: 'Talk to sales', href: CONTACT_SALES_URL };
  }

  const stepperBtn =
    'h-[30px] w-[30px] rounded-[9px] border border-white/18 text-[15px] leading-none text-white transition-colors hover:border-white/40 hover:bg-white/10';

  return (
    <div className="mt-7 flex flex-wrap items-center gap-x-10 gap-y-7 rounded-ref-lg border border-white/12 bg-white/[0.045] px-8 py-7">
      <div className="flex min-w-0 flex-[1.3_1_360px] flex-col gap-5">
        <span className="font-jetbrains text-[10px] tracking-[0.14em] text-white/50">
          ESTIMATE YOUR TIER &mdash; SAME LIMITS AS THE TABLE, NO SECOND COPY
        </span>
        <div className="flex flex-col gap-[9px]">
          <div className="flex items-baseline justify-between">
            <span className={`text-[13.5px] ${SOFT}`}>Active links</span>
            <span className="font-jetbrains text-sm font-semibold text-white tabular-nums">
              {fmt(links)}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={EST_LINK_STEPS.length - 1}
            step={1}
            value={linksIdx}
            onChange={(e) => setLinksIdx(Number(e.target.value))}
            aria-label="Estimated active links"
            className="w-full accent-brand-400"
          />
        </div>
        <div className="flex items-center justify-between">
          <span className={`text-[13.5px] ${SOFT}`}>Custom domains</span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setDomains((d) => Math.max(0, d - 1))}
              aria-label="Fewer domains"
              className={stepperBtn}
            >
              &minus;
            </button>
            <span className="min-w-4 text-center font-jetbrains text-sm font-semibold text-white">
              {domains}
            </span>
            <button
              type="button"
              onClick={() => setDomains((d) => Math.min(8, d + 1))}
              aria-label="More domains"
              className={stepperBtn}
            >
              +
            </button>
          </div>
        </div>
        <span className={`font-jetbrains text-[10px] tracking-[0.1em] ${DIM}`}>
          NO CLICKS INPUT &mdash; TRAFFIC IS NOT A DIMENSION HERE
        </span>
      </div>

      <div className="hidden w-px self-stretch bg-white/12 md:block" />

      <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-2">
        <span className="font-jetbrains text-[10px] tracking-[0.14em] text-white/50">
          THAT FITS
        </span>
        {/* Remount on tier change → verdict stamps in. */}
        <span
          key={tier}
          className={`animate-price-pop font-onest text-[34px] font-extrabold tracking-[-0.02em] motion-reduce:animate-none ${tierColor}`}
        >
          {tier}
        </span>
        <p className={`text-[13.5px] leading-[1.6] text-pretty ${SOFT}`}>{why}</p>
        <a
          href={cta.href}
          className="group mt-1 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-brand-400 transition-colors hover:text-brand-300"
        >
          {cta.label}
          <span
            aria-hidden
            className="inline-block transition-transform duration-200 group-hover:translate-x-0.5"
          >
            &rarr;
          </span>
        </a>
      </div>
    </div>
  );
}

/** Ink instrument band: the five numbers that gate the plans — plus the one
 *  deliberate non-limit (traffic) — explained as instrument cells before the
 *  comparison table uses them, with the tier estimator below. */
export default function UsageDimensionsSection() {
  return (
    <GridSection id="dimensions" className="bg-ink py-[88px] text-white">
      <AnimateOnScroll animation="fade-up">
        <div data-grid-block>
          <span className="block font-jetbrains text-[10px] tracking-[0.15em] text-white/50">
            BEFORE THE TABLE &mdash; WHAT THE NUMBERS MEASURE
          </span>
          <h2 className="mt-4 font-onest text-[clamp(26px,3.2vw,38px)] font-extrabold leading-[1.1] tracking-[-0.02em]">
            Five numbers gate the plans.
            <br />
            <span className="text-white/55">The sixth — your traffic — never will.</span>
          </h2>
          <p className="mt-4 font-jetbrains text-[11px] tracking-[0.13em] text-brand-400">
            THE ONLY MONTHLY NUMBER ON THIS PAGE IS THE PRICE.
          </p>
        </div>
      </AnimateOnScroll>

      <AnimateOnScroll animation="fade-up" delay={80}>
        {/* 1px gaps over a white/9 wash draw the hairline lattice between cells. */}
        <div
          data-grid-block
          className="mt-10 grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-px border border-white/[0.09] bg-white/[0.09]"
        >
          {USAGE_DIMENSIONS.map((dim) => (
            <InstrumentCell key={dim.term} dim={dim} />
          ))}
        </div>
      </AnimateOnScroll>

      <AnimateOnScroll animation="fade-up" delay={120}>
        <div data-grid-block>
          <Estimator />
        </div>
      </AnimateOnScroll>
    </GridSection>
  );
}
